'use client';

import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Settings, Scale, Pilcrow, ScrollText, BookText, Library, Gavel, Check, Palette, Sparkles, RotateCcw, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { APP_VERSION, isProdBuild } from '@/lib/version';
import {
  useSettings,
  ASSISTANT_ICONS,
  DARK_THEMES,
  LIGHT_THEMES,
  SKINS,
  BG_PATTERNS,
  AssistantIconId,
  DarkThemeId,
  LightThemeId,
  SkinId,
  BgPatternId,
} from '@/lib/hooks/use-settings';
import { PisrsIcon } from '@/components/icons/PisrsIcon';
import { ServicesStatus } from './ServicesStatus';
import { VoiceSettings } from './VoiceSettings';
import {
  MoneyIcon,
  ClockIcon,
  ChartIcon,
  StarIcon,
  BellIcon,
  CheckCircleIcon,
  ShieldIcon,
  FlameIcon,
  TribonijanIcon,
} from '@/components/icons/ColorfulIcons';

// Map icon IDs to components (Lucide + custom + colorful)
const IconComponents: Record<AssistantIconId, React.ComponentType<{ className?: string }>> = {
  // Standard icons
  pisrs: PisrsIcon,
  scale: Scale,
  pilcrow: Pilcrow,
  scrollText: ScrollText,
  bookText: BookText,
  library: Library,
  gavel: Gavel,
  // Colorful icons
  money: MoneyIcon,
  clock: ClockIcon,
  chart: ChartIcon,
  star: StarIcon,
  bell: BellIcon,
  checkCircle: CheckCircleIcon,
  shield: ShieldIcon,
  flame: FlameIcon,
  tribonijan: TribonijanIcon,
};

// Theme button component for cleaner code (works with both light and dark themes)
function ThemeButton({
  theme,
  isSelected,
  onClick,
  compact = false
}: {
  theme: { label: string; description: string; background: string; card: string };
  isSelected: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all',
          'hover:bg-muted/50',
          isSelected
            ? 'border-primary bg-primary/5'
            : 'border-transparent bg-muted/30'
        )}
      >
        {/* Color preview */}
        <div className="relative h-8 w-8 rounded-md border border-border flex-shrink-0 overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ backgroundColor: theme.background }}
          />
          <div
            className="absolute bottom-0 right-0 w-3 h-3 rounded-tl-sm"
            style={{ backgroundColor: theme.card }}
          />
          {isSelected && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <Check className="h-4 w-4 text-white drop-shadow-md" />
            </div>
          )}
        </div>
        <div className="text-[10px] font-medium truncate max-w-full">{theme.label}</div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 p-2.5 rounded-lg border-2 transition-all text-left',
        'hover:bg-muted/50',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-transparent bg-muted/30'
      )}
    >
      {/* Color preview with layered effect */}
      <div className="relative h-8 w-8 rounded-md border border-border flex-shrink-0 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ backgroundColor: theme.background }}
        />
        <div
          className="absolute bottom-0 right-0 w-4 h-4 rounded-tl-md"
          style={{ backgroundColor: theme.card }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{theme.label}</div>
        <div className="text-xs text-muted-foreground truncate">{theme.description}</div>
      </div>
      {isSelected && (
        <Check className="h-4 w-4 text-primary flex-shrink-0" />
      )}
    </button>
  );
}

interface SettingsDialogProps {
  trigger?: React.ReactNode;
}

export function SettingsDialog({ trigger }: SettingsDialogProps) {
  const { settings, setAssistantIcon, setDarkTheme, setLightTheme, setSkin, setBgPattern } = useSettings();
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const [restarting, setRestarting] = useState(false);

  // Allow opening from UserMenu via custom event
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-settings', handler);
    return () => window.removeEventListener('open-settings', handler);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Settings className="h-4 w-4" />
            <span className="sr-only">Nastavitve</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Nastavitve</DialogTitle>
          <DialogDescription>
            Prilagodi videz aplikacije
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(85vh-120px)] pr-4">
          <div className="space-y-6 py-4">
            {/* Slog vmesnika (skin) — smer vizualnega jezika */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Slog vmesnika
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(SKINS) as SkinId[]).map((skinId) => {
                  const skin = SKINS[skinId];
                  const isSelected = settings.skin === skinId;
                  return (
                    <button
                      key={skinId}
                      type="button"
                      onClick={() => setSkin(skinId)}
                      className={cn(
                        'relative flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left',
                        'hover:bg-muted/50',
                        isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/30'
                      )}
                    >
                      <div
                        className="h-8 w-8 rounded-full flex-shrink-0 border border-border"
                        style={{ background: `radial-gradient(circle at 35% 30%, ${skin.brand}, color-mix(in oklab, ${skin.brand} 50%, #000))` }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" style={{ fontFamily: 'var(--font-cormorant), serif', letterSpacing: '0.03em' }}>{skin.label}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{skin.description}</div>
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {/* Dekorativno ozadje */}
              <div className="grid grid-cols-4 gap-1.5 pt-1">
                {(Object.keys(BG_PATTERNS) as BgPatternId[]).map((bgId) => {
                  const bg = BG_PATTERNS[bgId];
                  const isSelected = settings.bgPattern === bgId;
                  return (
                    <button
                      key={bgId}
                      type="button"
                      onClick={() => setBgPattern(bgId)}
                      title={bg.description}
                      className={cn(
                        'flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all',
                        'hover:bg-muted/50',
                        isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/30'
                      )}
                    >
                      <div className="text-[11px] font-medium">{bg.label}</div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Naglasna barva in ozadje prve strani. Barvno ozadje izbereš spodaj.
              </p>
            </div>

            {/* Assistant Icon Selection with Tabs */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Ikona pomočnika</Label>
              <Tabs defaultValue="standard" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-3">
                  <TabsTrigger value="standard" className="gap-1.5">
                    <Scale className="h-3.5 w-3.5" />
                    Klasične
                  </TabsTrigger>
                  <TabsTrigger value="colorful" className="gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    Barvne
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="standard" className="mt-0">
                  <div className="grid grid-cols-4 gap-2">
                    {(Object.keys(ASSISTANT_ICONS) as AssistantIconId[])
                      .filter(id => !ASSISTANT_ICONS[id].colorful)
                      .map((iconId) => {
                        const icon = ASSISTANT_ICONS[iconId];
                        const IconComponent = IconComponents[iconId];
                        const isSelected = settings.assistantIcon === iconId;

                        return (
                          <button
                            key={iconId}
                            onClick={() => setAssistantIcon(iconId)}
                            className={cn(
                              'relative flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all',
                              'hover:bg-muted/50',
                              isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-transparent bg-muted/30'
                            )}
                          >
                            {isSelected && (
                              <div className="absolute top-1 right-1">
                                <Check className="h-3 w-3 text-primary" />
                              </div>
                            )}
                            <div className={cn(
                              'h-9 w-9 rounded-full flex items-center justify-center',
                              'bg-primary text-primary-foreground'
                            )}>
                              <IconComponent className="h-4 w-4" />
                            </div>
                            <div className="text-center">
                              <div className="text-[11px] font-medium leading-tight">{icon.label}</div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </TabsContent>

                <TabsContent value="colorful" className="mt-0">
                  <div className="grid grid-cols-4 gap-2">
                    {(Object.keys(ASSISTANT_ICONS) as AssistantIconId[])
                      .filter(id => ASSISTANT_ICONS[id].colorful)
                      .map((iconId) => {
                        const icon = ASSISTANT_ICONS[iconId];
                        const IconComponent = IconComponents[iconId];
                        const isSelected = settings.assistantIcon === iconId;

                        return (
                          <button
                            key={iconId}
                            onClick={() => setAssistantIcon(iconId)}
                            className={cn(
                              'relative flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all',
                              'hover:bg-muted/50',
                              isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-transparent bg-muted/30'
                            )}
                          >
                            {isSelected && (
                              <div className="absolute top-1 right-1">
                                <Check className="h-3 w-3 text-primary" />
                              </div>
                            )}
                            {/* Colorful icons on dark background to pop */}
                            <div className="h-9 w-9 rounded-full flex items-center justify-center bg-gray-900 dark:bg-gray-950">
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <div className="text-center">
                              <div className="text-[11px] font-medium leading-tight">{icon.label}</div>
                              <div className="text-[10px] text-muted-foreground">{icon.description}</div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Light Theme Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Palette className="h-4 w-4" />
                🌞 Svetlo ozadje
              </Label>
              <Tabs defaultValue="light-neutrals" className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-3 h-auto">
                  <TabsTrigger value="light-neutrals" className="text-[10px] px-1">Nevtralne</TabsTrigger>
                  <TabsTrigger value="light-blues" className="text-[10px] px-1">Modre</TabsTrigger>
                  <TabsTrigger value="light-greens" className="text-[10px] px-1">Zelene</TabsTrigger>
                  <TabsTrigger value="light-purples" className="text-[10px] px-1">Vijolične</TabsTrigger>
                </TabsList>
                <TabsList className="grid w-full grid-cols-4 mb-3 h-auto">
                  <TabsTrigger value="light-pinks" className="text-[10px] px-1">Roza</TabsTrigger>
                  <TabsTrigger value="light-warm" className="text-[10px] px-1">Tople</TabsTrigger>
                  <TabsTrigger value="light-yellow" className="text-[10px] px-1">Rumene</TabsTrigger>
                  <TabsTrigger value="light-special" className="text-[10px] px-1">Posebne</TabsTrigger>
                </TabsList>

                <TabsContent value="light-neutrals" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['white', 'snow', 'ivory', 'silver'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-blues" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['skyBlue', 'babyBlue', 'ice', 'azure'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-greens" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['mint', 'sage', 'honeydew', 'pistachio', 'aqua', 'seafoam'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-purples" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['lavender', 'lilac', 'periwinkle', 'wisteria'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-pinks" className="mt-0">
                  <div className="grid grid-cols-5 gap-1.5">
                    {(['blush', 'rose', 'peony', 'sakura', 'bubblegum'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-warm" className="mt-0">
                  <div className="grid grid-cols-3 gap-1.5">
                    {(['peach', 'apricot', 'coral'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-yellow" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['cream', 'vanilla', 'lemon', 'champagne'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-special" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['sand', 'linen', 'parchment', 'cotton'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
              <p className="text-xs text-muted-foreground">
                * Vidno v svetlem načinu
              </p>
            </div>

            {/* Dark Theme Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Palette className="h-4 w-4" />
                🌙 Temno ozadje
              </Label>
              <Tabs defaultValue="dark-neutrals" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-3">
                  <TabsTrigger value="dark-neutrals" className="text-xs">Nevtralne</TabsTrigger>
                  <TabsTrigger value="dark-cool" className="text-xs">Hladne</TabsTrigger>
                  <TabsTrigger value="dark-warm" className="text-xs">Tople</TabsTrigger>
                </TabsList>

                <TabsContent value="dark-neutrals" className="mt-0">
                  <div className="grid grid-cols-2 gap-2">
                    {(['black', 'charcoal', 'graphite', 'blue'] as DarkThemeId[]).map((themeId) => {
                      const theme = DARK_THEMES[themeId];
                      const isSelected = settings.darkTheme === themeId;

                      return (
                        <ThemeButton
                          key={themeId}
                          theme={theme}
                          isSelected={isSelected}
                          onClick={() => setDarkTheme(themeId)}
                        />
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="dark-cool" className="mt-0">
                  <div className="grid grid-cols-2 gap-2">
                    {(['navy', 'teal', 'forest', 'emerald', 'purple', 'grape'] as DarkThemeId[]).map((themeId) => {
                      const theme = DARK_THEMES[themeId];
                      const isSelected = settings.darkTheme === themeId;

                      return (
                        <ThemeButton
                          key={themeId}
                          theme={theme}
                          isSelected={isSelected}
                          onClick={() => setDarkTheme(themeId)}
                        />
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="dark-warm" className="mt-0">
                  <div className="grid grid-cols-2 gap-2">
                    {(['rose', 'wine', 'amber'] as DarkThemeId[]).map((themeId) => {
                      const theme = DARK_THEMES[themeId];
                      const isSelected = settings.darkTheme === themeId;

                      return (
                        <ThemeButton
                          key={themeId}
                          theme={theme}
                          isSelected={isSelected}
                          onClick={() => setDarkTheme(themeId)}
                        />
                      );
                    })}
                  </div>
                </TabsContent>
              </Tabs>
              <p className="text-xs text-muted-foreground">
                * Vidno v temnem načinu
              </p>
            </div>

            {/* Glas pomočnika — izbira TTS glasu + preizkus */}
            <div className="pt-4 border-t border-border/50">
              <VoiceSettings />
            </div>

            {/* Stanje storitev — API health dashboard */}
            <div className="pt-4 border-t border-border/50">
              <ServicesStatus />
            </div>

            {/* O aplikaciji — verzija + build podpis (sledljivost prod/dev) */}
            <div className="space-y-2 pt-4 border-t border-border/50">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Info className="h-4 w-4" />
                O aplikaciji
              </Label>
              <div className="rounded-lg bg-muted/30 p-3 text-xs space-y-1.5 font-mono">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Okolje</span>
                  <span className={cn('truncate', isProdBuild ? '' : 'text-amber-600 dark:text-amber-400')}>
                    {isProdBuild ? 'PROD · app.sersen.eu' : 'DEV · dev.sersen.eu'}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Verzija</span>
                  <span>v{APP_VERSION.version}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Veja</span>
                  <span className="truncate max-w-[60%]">{APP_VERSION.branch}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Commit</span>
                  <span>{APP_VERSION.sha}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Build</span>
                  <span>{new Date(APP_VERSION.builtAt).toLocaleString('sl-SI')}</span>
                </div>
              </div>
            </div>

            {/* Dev Tools — samo admin (ADMIN_EMAILS) in samo v dev (prod teče prek systemd) */}
            {session?.user?.isAdmin && !isProdBuild && (
              <div className="space-y-3 pt-4 border-t border-dashed border-amber-500/30">
                <Label className="text-sm font-medium flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <RotateCcw className="h-4 w-4" />
                  Dev orodja
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                  disabled={restarting}
                  onClick={async () => {
                    setRestarting(true);
                    try {
                      const res = await fetch('/api/dev/restart', { method: 'POST' });
                      if (res.ok) {
                        // Server will restart in ~500ms, poll until it's back
                        setTimeout(() => {
                          const poll = setInterval(async () => {
                            try {
                              const health = await fetch('/api/health', { signal: AbortSignal.timeout(2000) });
                              if (health.ok) {
                                clearInterval(poll);
                                window.location.reload();
                              }
                            } catch {
                              // Server still restarting
                            }
                          }, 1500);
                          // Stop polling after 30s
                          setTimeout(() => clearInterval(poll), 30000);
                        }, 1500);
                      } else {
                        setRestarting(false);
                        alert('Restart ni uspel: ' + (await res.text()));
                      }
                    } catch {
                      setRestarting(false);
                    }
                  }}
                >
                  {restarting ? (
                    <>
                      <RotateCcw className="h-3.5 w-3.5 mr-2 animate-spin" />
                      Restartiram...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-3.5 w-3.5 mr-2" />
                      Ponovni zagon serverja
                    </>
                  )}
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  Zahteva dev-wrapper.sh. Server se bo ustavil in samodejno zagnal.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Export the icon component getter for use in other components
 */
export function getAssistantIcon(iconId: AssistantIconId): React.ComponentType<{ className?: string }> {
  return IconComponents[iconId];
}
